import React from 'react';
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsByLocation,
    EBirdObservationsBySpecies
} from "ebird-mydata-reader";
import {AppScreen} from "./app-screen";
import Welcome from "./views/welcome";
import UploadData from "./views/upload-data";
import SetHomeLocation from "./views/set-home-location";
import SelectTimePeriod from "./views/select-time-period";
import MapExplorer from "./views/map";
import WrapUp from "./views/wrap-up";
import BigYearExplorer from "./views/big-year-explorer";
import FamilyExplorer from "./views/family-explorer";
import Discover from "./views/discover";
import {EBirdTaxonomyEntry} from "./helpers/useEBirdTaxonomy";
import LoadingData from "./views/loading-data";
import type {HomeLocation, SpeciesFamilyLink} from "./App";
import type {EnrichedObservationsByLocation} from "./helpers/types";
import type {DashboardSummary} from "./helpers/birdDataCache";

export type AppRouterProps = {
    screen: AppScreen,
    rawData: EBirdMyDataSchema[],
    lifeList: EBirdMyDataSchema[],
    years: number[]|string[],
    months: number[]|string[],
    year?: number|string,
    month?: number|string,
    mapMode?: string,
    mapTour?: boolean,
    mapActiveSpecies?: number,
    filteredObservations: EBirdMyDataSchema[],
    observationsBySpecies: EBirdObservationsBySpecies[],
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation>,
    homeLocation?: HomeLocation,
    checklist: EBirdChecklistByFamily[],
    dashboardSummary?: DashboardSummary,
    familyByTaxonomicOrder: Map<number, SpeciesFamilyLink>,
    parentTaxonomicOrderByTaxonomicOrder: Map<number, number>,
    speciesImages: any,
    getSpeciesImages: (markers: any) => void,
    lookupByTaxonomicOrder: (taxonomicOrder: number) => EBirdTaxonomyEntry|undefined,
    onRequestData: () => void,
    onShowUploadData: () => void,
    onDataLoaded: (data: any[]) => void,
    onSetYears: React.Dispatch<any>,
    onResetDataRequest: () => void,
    onSetMonth: React.Dispatch<any>,
    onSetYear: React.Dispatch<any>,
    onSetTimePeriod: (year: string, month: string) => void,
    onSetSpeciesFilter: (taxonomicOrder: number) => EBirdObservationsBySpecies|undefined,
    onSetHomeLocation: (location: HomeLocation) => void,
    onSkipHomeLocation: () => void,
    onResetStoredData: () => void,
    onStartBigYear: (year?: string) => void,
    onShowWrapUp: () => void,
    onShowTimePeriodSelector: () => void,
    onShowHomeLocation: () => void,
    onShowFamilyExplorer: () => void
}

export default function AppRouter({
    screen,
    rawData,
    lifeList,
    years,
    months,
    year,
    month,
    mapMode,
    mapTour,
    mapActiveSpecies,
    filteredObservations,
    observationsBySpecies,
    observationsByLocation,
    homeLocation,
    checklist,
    dashboardSummary,
    familyByTaxonomicOrder,
    parentTaxonomicOrderByTaxonomicOrder,
    speciesImages,
    getSpeciesImages,
    lookupByTaxonomicOrder,
    onRequestData,
    onShowUploadData,
    onDataLoaded,
    onSetYears,
    onResetDataRequest,
    onSetMonth,
    onSetYear,
    onSetTimePeriod,
    onSetHomeLocation,
    onSkipHomeLocation,
    onResetStoredData,
    onStartBigYear,
    onShowWrapUp,
    onShowTimePeriodSelector,
    onShowHomeLocation,
    onShowFamilyExplorer
}: AppRouterProps) {
    switch (screen) {
        case AppScreen.Loading:
            return <LoadingData />;

        case AppScreen.Welcome:
            return <Welcome onRequestData={onRequestData} onShowUploadData={onShowUploadData} />;

        case AppScreen.Upload:
            return (
                <UploadData
                    onSetData={onDataLoaded}
                    onSetYears={onSetYears}
                    onResetDataRequest={onResetDataRequest}
                />
            );

        case AppScreen.HomeLocation:
            return (
                <SetHomeLocation
                    observationsByLocation={observationsByLocation}
                    selectedHomeLocation={homeLocation}
                    onSetHomeLocation={onSetHomeLocation}
                    onSkipHomeLocation={onSkipHomeLocation}
                />
            );

        case AppScreen.SelectTimePeriod:
            return (
                <SelectTimePeriod
                    homeLocation={homeLocation}
                    dashboardSummary={dashboardSummary}
                    lifeListLength={lifeList.length}
                    speciesImages={speciesImages}
                    years={years}
                    months={months}
                    onSetMonth={onSetMonth}
                    onSetYear={onSetYear}
                    onSetTimePeriod={onSetTimePeriod}
                    onResetStoredData={onResetStoredData}
                    onShowWrapUp={onShowWrapUp}
                    onShowHomeLocation={onShowHomeLocation}
                    onShowFamilyExplorer={onShowFamilyExplorer}
                />
            );

        case AppScreen.Map:
            return (
                <MapExplorer
                    observations={filteredObservations}
                    checklist={checklist}
                    year={year}
                    month={month}
                    homeLocation={homeLocation}
                    observationsByLocation={observationsByLocation}
                    familyByTaxonomicOrder={familyByTaxonomicOrder}
                    parentTaxonomicOrderByTaxonomicOrder={parentTaxonomicOrderByTaxonomicOrder}
                    onStartBigYear={onStartBigYear}
                    onShowTimePeriodSelector={onShowTimePeriodSelector}
                    mapMode={mapMode}
                    mapTour={mapTour}
                    activeSpecies={mapActiveSpecies}
                    speciesImages={speciesImages}
                    getSpeciesImages={getSpeciesImages}
                    lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                />
            );

        case AppScreen.WrapUp:
            return (
                <WrapUp
                    observations={filteredObservations}
                    year={year}
                    month={month}
                />
            );

        case AppScreen.Discover:
            return (
                <Discover
                    rawData={rawData}
                    lifeList={lifeList}
                    observationsByLocation={observationsByLocation}
                    checklist={checklist}
                    years={years}
                    homeLocation={homeLocation}
                    speciesImages={speciesImages}
                    onStartBigYear={onStartBigYear}
                />
            );

        case AppScreen.BigYear:
            return (
                <BigYearExplorer
                    observations={filteredObservations}
                    speciesImages={speciesImages}
                    year={year}
                    familyByTaxonomicOrder={familyByTaxonomicOrder}
                    lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                />
            );

        case AppScreen.FamilyExplorer:
            return <FamilyExplorer checklist={checklist} observationsBySpecies={observationsBySpecies} />;
    }
}
